import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAdmin, adminAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Save, PlugZap, CheckCircle2, XCircle, Mail, MessageSquare,
  Database, Settings as SettingsIcon, Send, Lock, KeyRound,
} from "lucide-react";

const inputCls = "bg-white/5 border-white/15 rounded-sm font-mono-tech h-10 text-white";

function Section({ title, children, icon: Icon }) {
  return (
    <section className="panel p-5 space-y-4" data-testid={`section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon ? <Icon className="w-4 h-4 text-zinc-400" /> : null}
        <h3 className="font-heading font-semibold text-base tracking-tight">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid ? `field-${testid}` : undefined}>
      <Label className="label-tech text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}

function PadlockGate({ onUnlocked }) {
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/admin/unlock`, { username: "admin", password: pw });
      onUnlocked(r.data.admin_token);
      toast.success("Settings unlocked");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Wrong password");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center text-white px-6" data-testid="padlock-gate">
      <div className="panel p-8 w-full max-w-sm space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-amber-400/30 bg-amber-500/10 flex items-center justify-center rounded-sm">
            <Lock className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-base">SETTINGS LOCKED</h1>
            <div className="label-tech text-[10px] text-zinc-500">Admin password required</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid="padlock-input"
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Enter admin password"
            className={inputCls}
          />
          {err && (
            <div className="flex items-center gap-2 text-xs text-red-300 border border-red-500/30 bg-red-500/10 rounded-sm px-3 py-2">
              <XCircle className="w-3 h-3" /> {err}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              data-testid="padlock-back"
              type="button"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-zinc-400 hover:text-white rounded-sm h-10 flex-1"
            >
              <ArrowLeft className="w-3 h-3 mr-2" /> BACK
            </Button>
            <Button
              data-testid="padlock-submit"
              type="submit"
              disabled={busy || !pw}
              className="bg-white text-black hover:bg-white/90 rounded-sm h-10 flex-1 disabled:opacity-40"
            >
              <KeyRound className="w-3 h-3 mr-2" />
              {busy ? "VERIFYING…" : "UNLOCK"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { unlocked, unlock, lock } = useAdmin();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testWa, setTestWa] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((r) => setS(r.data))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setN = (k, v) => set(k, v === "" ? "" : Number(v));

  const updateSlab = (i, k, v) => {
    const slabs = [...(s.tod_slabs || [])];
    slabs[i] = { ...slabs[i], [k]: k === "rate" ? Number(v) : v };
    set("tod_slabs", slabs);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      // Don't ship the masked password back
      if (payload.mysql_password === "••••••••") delete payload.mysql_password;
      const r = await axios.put(`${API}/settings`, payload, adminAuth());
      setS(r.data.settings);
      toast.success("Settings saved");
    } catch (e) {
      if (e?.response?.status === 401) { lock(); toast.error("Session expired — please unlock again"); }
      else toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/settings/test-connection`, {
        mysql_host: s.mysql_host || "",
        mysql_port: Number(s.mysql_port) || 3306,
        mysql_user: s.mysql_user || "",
        mysql_password: s.mysql_password === "••••••••" ? "" : (s.mysql_password || ""),
        mysql_db: s.mysql_db || "",
        mysql_table: s.mysql_table || "",
      }, adminAuth());
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Connection OK · ${r.data.row_count ?? "?"} rows`);
      else toast.error(r.data.error || "Connection failed");
    } catch (e) {
      if (e?.response?.status === 401) { lock(); toast.error("Session expired — please unlock again"); return; }
      const msg = e?.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const sendTest = async (channel) => {
    const to = channel === "email"
      ? testEmail.split(",").map((x) => x.trim()).filter(Boolean)
      : testWa.split(",").map((x) => x.trim()).filter(Boolean);
    if (to.length === 0) {
      toast.error(`Enter at least one ${channel === "email" ? "email" : "phone number"}`);
      return;
    }
    try {
      const r = await axios.post(`${API}/notifications/test`, {
        channel, to,
        subject: "EMS Dashboard test",
        body: "This is a test alert from your Energy Management dashboard.",
      });
      if (r.data.ok) toast.success(`${channel} sent`);
      else toast.error(r.data.reason || r.data.error || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  if (!unlocked) return <PadlockGate onUnlocked={unlock} />;

  if (loading || !s) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400 label-tech">LOADING…</div>;
  }

  return (
    <div className="min-h-screen text-white" data-testid="settings-root">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/[0.03] border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="back-btn" onClick={() => navigate("/dashboard")} variant="ghost"
                    className="text-zinc-400 hover:text-white rounded-sm h-8">
              <ArrowLeft className="w-3 h-3 mr-2" /> DASHBOARD
            </Button>
            <h1 className="font-heading font-bold text-base">SETTINGS</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="lock-btn" onClick={() => { lock(); navigate("/dashboard"); }} variant="ghost"
                    className="text-zinc-400 hover:text-amber-300 rounded-sm h-9">
              <Lock className="w-3 h-3 mr-2" /> LOCK
            </Button>
            <Button data-testid="save-settings-btn" onClick={save} disabled={saving}
                    className="bg-white text-black hover:bg-white/90 rounded-sm h-9">
              <Save className="w-3 h-3 mr-2" />
              {saving ? "SAVING…" : "SAVE CHANGES"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-6 space-y-5">
        {/* MySQL connection */}
        <Section title="MySQL Database (Live Telemetry)" icon={Database}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Host" testid="mysql_host">
              <Input value={s.mysql_host || ""} onChange={(e) => set("mysql_host", e.target.value)}
                     className={inputCls} placeholder="neo.herosite.pro" />
            </Field>
            <Field label="Port" testid="mysql_port">
              <Input type="number" value={s.mysql_port ?? ""} onChange={(e) => setN("mysql_port", e.target.value)}
                     className={inputCls} placeholder="3306" />
            </Field>
            <Field label="User" testid="mysql_user">
              <Input value={s.mysql_user || ""} onChange={(e) => set("mysql_user", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Password" testid="mysql_password">
              <Input value={s.mysql_password || ""} onChange={(e) => set("mysql_password", e.target.value)}
                     className={inputCls} placeholder="••••••••"
                     onFocus={(e) => { if (e.target.value === "••••••••") set("mysql_password", ""); }} />
            </Field>
            <Field label="Database" testid="mysql_db">
              <Input value={s.mysql_db || ""} onChange={(e) => set("mysql_db", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Table" testid="mysql_table">
              <Input value={s.mysql_table || ""} onChange={(e) => set("mysql_table", e.target.value)}
                     className={inputCls} placeholder="Db_Secure_Elite_new" />
            </Field>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button data-testid="test-connection-btn" onClick={testConnection} disabled={testing}
                    variant="outline" className="border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 rounded-sm h-9">
              <PlugZap className="w-3.5 h-3.5 mr-2" />
              {testing ? "TESTING…" : "TEST CONNECTION"}
            </Button>
            {testResult && (testResult.ok
              ? <span className="label-tech text-emerald-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> OK · {testResult.row_count ?? "?"} rows
                </span>
              : <span className="label-tech text-red-400 inline-flex items-center gap-1 max-w-md truncate" title={testResult.error}>
                  <XCircle className="w-3 h-3" /> {testResult.error}
                </span>
            )}
          </div>
        </Section>

        {/* Tariff & Demand */}
        <Section title="Tariff & Demand" icon={SettingsIcon}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Energy Tariff (₹/kWh)" testid="tariff">
              <Input type="number" step="0.01" value={s.tariff ?? ""} onChange={(e) => setN("tariff", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Sanctioned Demand (kVA)" testid="sanctioned_kva">
              <Input type="number" step="1" value={s.sanctioned_kva ?? ""} onChange={(e) => setN("sanctioned_kva", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Demand Rate (₹/kVA)" testid="demand_rate">
              <Input type="number" step="0.01" value={s.demand_rate ?? ""} onChange={(e) => setN("demand_rate", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </Section>

        {/* Scaling */}
        <Section title="CT / PT Scaling Factors" icon={SettingsIcon}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Voltage L-N Multiplier" testid="vln_scale">
              <Input type="number" step="0.01" value={s.voltage_ln_scale ?? 1}
                     onChange={(e) => setN("voltage_ln_scale", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Voltage L-L Multiplier" testid="vll_scale">
              <Input type="number" step="0.01" value={s.voltage_ll_scale ?? 1}
                     onChange={(e) => setN("voltage_ll_scale", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Current Multiplier" testid="curr_scale">
              <Input type="number" step="0.01" value={s.current_scale ?? 1}
                     onChange={(e) => setN("current_scale", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </Section>

        {/* Thresholds */}
        <Section title="Alert Thresholds" icon={SettingsIcon}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["overvoltage_v", "Over-voltage (V)"],
              ["undervoltage_v", "Under-voltage (V)"],
              ["overcurrent_a", "Over-current (A)"],
              ["low_pf", "Low PF"],
              ["low_freq_hz", "Low Frequency (Hz)"],
            ].map(([k, lbl]) => (
              <Field key={k} label={lbl} testid={`threshold_${k}`}>
                <Input type="number" step="0.01" value={s.thresholds?.[k] ?? ""}
                       onChange={(e) => set("thresholds", { ...(s.thresholds || {}), [k]: Number(e.target.value) })}
                       className={inputCls} />
              </Field>
            ))}
          </div>
        </Section>

        {/* ToD slabs */}
        <Section title="Time-of-Day Tariff" icon={SettingsIcon}>
          <div className="flex items-center gap-3 mb-3">
            <Switch data-testid="tod-enabled-switch" checked={!!s.tod_enabled}
                    onCheckedChange={(v) => set("tod_enabled", v)} />
            <span className="label-tech">{s.tod_enabled ? "ENABLED" : "DISABLED"}</span>
          </div>
          <div className="space-y-3">
            {(s.tod_slabs || []).map((slab, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid={`tod-slab-${i}`}>
                <Input value={slab.label || ""} onChange={(e) => updateSlab(i, "label", e.target.value)}
                       placeholder="Label" className={inputCls} />
                <Input value={slab.start || ""} onChange={(e) => updateSlab(i, "start", e.target.value)}
                       placeholder="HH:MM" className={inputCls} />
                <Input value={slab.end || ""} onChange={(e) => updateSlab(i, "end", e.target.value)}
                       placeholder="HH:MM" className={inputCls} />
                <Input type="number" step="0.01" value={slab.rate ?? 0}
                       onChange={(e) => updateSlab(i, "rate", e.target.value)}
                       placeholder="Rate (₹/kWh)" className={inputCls} />
              </div>
            ))}
          </div>
        </Section>

        {/* Notifications */}
        <Section title="Email Alerts (Resend)" icon={Mail}>
          <Field label="Recipients (comma-separated emails)" testid="email_recipients">
            <Input value={s.notify_email_recipients || ""} onChange={(e) => set("notify_email_recipients", e.target.value)}
                   placeholder="alerts@example.com" className={inputCls} />
          </Field>
          <div className="flex items-center gap-3">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                   placeholder="Send test email to…" className={inputCls + " flex-1"} />
            <Button data-testid="send-test-email-btn" onClick={() => sendTest("email")} variant="outline"
                    className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-10">
              <Send className="w-3 h-3 mr-2" /> SEND TEST
            </Button>
          </div>
        </Section>

        <Section title="WhatsApp Alerts (Twilio)" icon={MessageSquare}>
          <Field label="Recipients (comma-separated phone numbers, +countrycode...)" testid="wa_recipients">
            <Input value={s.notify_whatsapp_recipients || ""} onChange={(e) => set("notify_whatsapp_recipients", e.target.value)}
                   placeholder="+919999999999" className={inputCls} />
          </Field>
          <div className="flex items-center gap-3">
            <Input value={testWa} onChange={(e) => setTestWa(e.target.value)}
                   placeholder="Send test WA to…" className={inputCls + " flex-1"} />
            <Button data-testid="send-test-wa-btn" onClick={() => sendTest("whatsapp")} variant="outline"
                    className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-10">
              <Send className="w-3 h-3 mr-2" /> SEND TEST
            </Button>
          </div>
        </Section>

        <div className="pb-12 flex justify-end">
          <Button data-testid="save-settings-btn-bottom" onClick={save} disabled={saving}
                  className="bg-white text-black hover:bg-white/90 rounded-sm h-10 px-6">
            <Save className="w-3 h-3 mr-2" />
            {saving ? "SAVING…" : "SAVE ALL CHANGES"}
          </Button>
        </div>
      </main>
    </div>
  );
}
